import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import Select from "react-select";
import { useAppServices } from "hook/services";
import { RoleOptions, RoleTypeOptions, UserPermissions } from "enums/user";

interface propTypes {
  editData: any; // existing user to edit
  handleRefresh:any;
  locations: any;
    toggle: any;
  show: boolean;
}

const UserEditModal = ({ editData, handleRefresh, locations,toggle,show }: propTypes) => {
  const AppService = useAppServices();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const payload = {
      _id: editData._id,
      username: values.username,
      email: values.email,
      phone: values.phone,
      roles: values.roles.map((r: any) => r.value),
      roleType: values.roleType.map((r: any) => r.value),
      permissions: values.permissions.map((p: any) => p.value),
      assignedLocations: values.assignedLocations.map((l: any) => l.value),
    };

    const { response, error } = await AppService.user.update({
      payload,
      toaster: true,
    });

    setLoading(false);

    if (response) {
      formik.resetForm();
      toggle();
      handleRefresh();
    }
  };

  const formik = useFormik({
    initialValues: {
      username: editData.username || "",
      email: editData.email || "",
      phone: editData.phone || "",
      roles:
        editData.roles?.map((r: string) => ({
          label: r,
          value: r,
        })) || [],
      roleType:
        editData.roleType?.map((r: string) => ({
          label: r,
          value: r,
        })) || [],
      permissions:
        editData.permissions?.map((p: string) => ({
          label: p,
          value: p,
        })) || [],
      assignedLocations:
        editData.assignedLocations?.map((loc: any) => ({
          label: loc.name,
          value: loc._id,
        })) || [],
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please enter username"),
      email: Yup.string().email("Invalid email").required("Please enter email"),
      phone: Yup.string().required("Please enter phone number"),
      roles: Yup.array().min(1, "Select at least one role"),
      roleType: Yup.array().min(1, "Select at least one role type"),
      permissions: Yup.array().min(1, "Select at least one permission"),
      assignedLocations: Yup.array().min(1, "Select at least one location"),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <>
      <button
        type="button"
        className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 focus:ring focus:ring-custom-100"
        onClick={toggle}
      >
        Edit
      </button>

      <Modal
        show={show}
        onHide={toggle}
        id="userEditModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[36rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Edit User
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block mb-1 text-sm font-medium">Username</label>
              <input
                type="text"
                name="username"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.username}
              />
              {formik.touched.username && formik.errors.username && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.username as string}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.email as string}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium">Phone</label>
              <input
                type="text"
                name="phone"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.phone as string}
                </p>
              )}
            </div>

            {/* Roles */}
            <div>
              <label className="block mb-1 text-sm font-medium">Roles</label>
              <Select
                isMulti
                name="roles"
                options={RoleOptions}
                value={formik.values.roles}
                onChange={(selected) => formik.setFieldValue("roles", selected)}
              />
              {formik.touched.roles && formik.errors.roles && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.roles as string}
                </p>
              )}
            </div>

            {/* Role Type */}
            <div>
              <label className="block mb-1 text-sm font-medium">Role Type</label>
              <Select
                isMulti
                name="roleType"
                options={RoleTypeOptions}
                value={formik.values.roleType}
                onChange={(selected) =>
                  formik.setFieldValue("roleType", selected)
                }
              />
              {formik.touched.roleType && formik.errors.roleType && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.roleType as string}
                </p>
              )}
            </div>

            {/* Permissions */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Permissions
              </label>
              <Select
                isMulti
                name="permissions"
                options={UserPermissions.map((item: any) => ({
                  label: item,
                  value: item,
                }))}
                value={formik.values.permissions}
                onChange={(selected) =>
                  formik.setFieldValue("permissions", selected)
                }
              />
              {formik.touched.permissions && formik.errors.permissions && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.permissions as string}
                </p>
              )}
            </div>

            {/* Assigned Locations */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Assigned Locations
              </label>
              <Select
                isMulti
                name="assignedLocations"
                options={locations.map((item: { name: string; _id: string }) => ({
                  label: item.name,
                  value: item._id,
                }))}
                value={formik.values.assignedLocations}
                onChange={(selected) =>
                  formik.setFieldValue("assignedLocations", selected)
                }
              />
              {formik.touched.assignedLocations &&
                formik.errors.assignedLocations && (
                  <p className="text-red-400 text-sm mt-1">
                    {formik.errors.assignedLocations as string}
                  </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                disabled={loading}
              >
                {loading ? "Saving..." : "Update User"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserEditModal;
